import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';

import {
  PhonebookContactVM,
  PhonebookListParams,
  PhonebookCountsVM,
  ContactType,
  ContactFormDialogData,
  DeleteConfirmDialogData,
  CreatePhonebookContactDTO,
  UpdatePhonebookContactDTO,
  PhonebookContactNoteVM,
} from '../../models';
import { PhonebookService } from '../../services/phonebook.service';
import { PhonebookSidebarService } from '../../services/phonebook-sidebar.service';
import { ToastService } from '../../services/toast.service';
import { CorporateFormsService } from '../../../services/corporate-forms.service';
import { IFormContact } from '../../../models/IFormContact.interface';
import { ContactFormDialogComponent } from '../contact-form-dialog/contact-form-dialog.component';
import { DeleteConfirmDialogComponent } from '../delete-confirm-dialog/delete-confirm-dialog.component';

type SidebarView = 'list' | 'detail';

interface FilterTab {
  key: string;
  label: string;
  count?: number;
}

@Component({
  selector: 'app-phonebook-sidebar',
  templateUrl: './phonebook-sidebar.component.html',
  styleUrls: ['./phonebook-sidebar.component.scss'],
})
export class PhonebookSidebarComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  /**
   * When true the phonebook renders as a standalone full-page view
   * (own browser tab) instead of the slide-in overlay sidebar.
   */
  @Input() pageMode = false;

  // Sidebar state
  isOpen = false;
  view: SidebarView = 'list';
  selectedContact: PhonebookContactVM | null = null;

  // Notes thread (loaded when a contact is selected)
  notes: PhonebookContactNoteVM[] = [];
  notesLoading = false;
  newNoteBody = '';
  addingNote = false;

  // List state
  contacts: PhonebookContactVM[] = [];
  total = 0;
  loading = false;
  loadingMore = false;

  // Form contacts (aggregated from submitted forms). Loaded once per open,
  // filtered client-side by search. Hidden when a specific filter tab is active.
  formContacts: PhonebookContactVM[] = [];
  private formContactsLoaded = false;

  // Search & filters
  search = '';
  activeFilter = 'all';
  page = 1;
  pageSize = 20;
  hasMore = false;

  // Counts
  counts: PhonebookCountsVM = {
    work: 0, client: 0, employee: 0, vendor: 0, consultant: 0,
    legal: 0, bank: 0, government: 0, familyMember: 0, general: 0, total: 0,
  };

  filters: FilterTab[] = [
    { key: 'all', label: 'All' },
    { key: 'Work', label: 'Work' },
    { key: 'Client', label: 'Client' },
    { key: 'Employee', label: 'Employee' },
    { key: 'Vendor', label: 'Vendor' },
    { key: 'Consultant', label: 'Consultant' },
    { key: 'Legal', label: 'Legal' },
    { key: 'Bank', label: 'Bank' },
    { key: 'Government', label: 'Gov' },
    { key: 'Family Member', label: 'Family' },
    { key: 'General', label: 'General' },
  ];

  // Search debounce
  private searchSubject = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(
    private phonebookService: PhonebookService,
    private sidebarService: PhonebookSidebarService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private formsService: CorporateFormsService,
  ) {}

  ngOnInit(): void {
    if (this.pageMode) {
      // Full-page mode: render immediately and load data, independent of
      // the sidebar toggle service (this lives in its own browser tab).
      this.isOpen = true;
      this.loadContacts();
      this.loadCounts();
      this.loadFormContacts();
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 200);
    } else {
      // Subscribe to sidebar open/close
      this.subs.push(
        this.sidebarService.isOpen$.subscribe((open) => {
          this.isOpen = open;
          if (open && this.contacts.length === 0) {
            this.loadContacts();
            this.loadCounts();
          }
          if (open && !this.formContactsLoaded) {
            this.loadFormContacts();
          }
          if (open) {
            setTimeout(() => this.searchInput?.nativeElement?.focus(), 200);
          }
          if (!open) {
            this.view = 'list';
            this.selectedContact = null;
          }
        })
      );

      // Subscribe to selected contact
      this.subs.push(
        this.sidebarService.selectedContact$.subscribe((c) => {
          if (c) {
            this.selectedContact = c;
            this.view = 'detail';
            this.loadNotes(c);
          }
        })
      );
    }

    // Debounced search
    this.subs.push(
      this.searchSubject
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value) => {
          this.search = value;
          this.page = 1;
          this.contacts = [];
          this.loadContacts();
        })
    );

    // Global "new contact" requests (from the app header, any page).
    // This component is mounted app-wide, so the dialog opens everywhere.
    this.subs.push(
      this.sidebarService.createRequested$.subscribe(() => this.openCreateDialog())
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  // ═══════════════════════════════════
  // Data
  // ═══════════════════════════════════

  loadContacts(append = false): void {
    if (!append) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }

    const params: PhonebookListParams = {
      search: this.search || undefined,
      type: this.activeFilter !== 'all' ? (this.activeFilter as ContactType) : undefined,
      page: this.page,
      pageSize: this.pageSize,
      sortBy: 'firstName',
      sortDirection: 'asc',
    };

    this.phonebookService
      .list(params)
      .pipe(finalize(() => { this.loading = false; this.loadingMore = false; }))
      .subscribe({
        next: (res) => {
          if (append) {
            this.contacts = [...this.contacts, ...res.items];
          } else {
            this.contacts = res.items;
          }
          this.total = res.total;
          this.hasMore = this.contacts.length < res.total;
        },
        error: () => this.toastService.error('Failed to load contacts'),
      });
  }

  loadFormContacts(): void {
    this.formsService.getFormContacts().subscribe({
      next: (items: IFormContact[]) => {
        this.formContacts = (items || [])
          .filter(fc => !!fc.submittedFormId && !!(fc.displayName || fc.company))
          .map<PhonebookContactVM>(fc => {
            const parts = (fc.displayName || '').trim().split(/\s+/);
            const firstName = parts[0] || (fc.displayName || fc.company || '').trim();
            const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
            return {
              id: -fc.submittedFormId,
              firstName,
              lastName,
              email: this.normalizeFormContactEmail(fc.email),
              mobilePhone: this.normalizeFormContactPhone(fc.phoneNumber),
              workPhone: null,
              company: fc.company,
              contactType: 'Client',
              jobTitle: null,
              department: null,
              address: null,
              city: null,
              country: null,
              tic: null,
              vatNumber: null,
              notes: null,
              preferredContactMethods: null,
              leadRating: null,
              industry: null,
              ownerAzureObjectId: null,
              ownerName: null,
              introducerId: null,
              introducerName: null,
              linkedInUrl: null,
              website: null,
              facebook: null,
              instagram: null,
              createdAt: '',
              updatedAt: null,
              isFormContact: true,
              formContactType: fc.type,
            };
          });
        this.formContactsLoaded = true;
      },
      error: () => {
        // Silent fail — form contacts are additive; phonebook still works without them.
        this.formContactsLoaded = true;
      },
    });
  }

  /**
   * Form contacts arrive with phoneNumber serialized as JSON:
   *   [{"code":"+357","phone":25750469}]
   * Flatten it to a readable "+357 25750469" string; accept plain strings too.
   */
  private normalizeFormContactPhone(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (trimmed[0] !== '[' && trimmed[0] !== '{') return trimmed;
    try {
      const parsed = JSON.parse(trimmed);
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      const pieces = entries
        .map((e: any) => {
          const code = e?.code != null ? String(e.code).trim() : '';
          const phone = e?.phone != null ? String(e.phone).trim() : '';
          return [code, phone].filter(Boolean).join(' ').trim();
        })
        .filter((s: string) => s.length > 0);
      return pieces.length ? pieces.join(', ') : null;
    } catch {
      return trimmed;
    }
  }

  /** Some submitted-form answers serialize emails the same way (array of objects). */
  private normalizeFormContactEmail(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    if (trimmed[0] !== '[' && trimmed[0] !== '{') return trimmed;
    try {
      const parsed = JSON.parse(trimmed);
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      const pieces = entries
        .map((e: any) => {
          if (typeof e === 'string') return e.trim();
          return (e?.email || e?.address || '').toString().trim();
        })
        .filter((s: string) => s.length > 0);
      return pieces.length ? pieces.join(', ') : null;
    } catch {
      return trimmed;
    }
  }

  /** Form contacts filtered by the current search term. Hidden when a specific type filter is active. */
  get visibleFormContacts(): PhonebookContactVM[] {
    if (this.activeFilter !== 'all') return [];
    if (!this.search) return this.formContacts;
    const q = this.search.toLowerCase();
    return this.formContacts.filter(c =>
      (c.firstName || '').toLowerCase().includes(q) ||
      (c.lastName || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.company || '').toLowerCase().includes(q) ||
      (c.mobilePhone || '').toLowerCase().includes(q)
    );
  }

  loadCounts(): void {
    this.phonebookService.getCounts().subscribe({
      next: (counts) => {
        this.counts = counts;
        this.filters = [
          { key: 'all', label: 'All', count: counts.total },
          { key: 'Work', label: 'Work', count: counts.work },
          { key: 'Client', label: 'Client', count: counts.client },
          { key: 'Employee', label: 'Employee', count: counts.employee },
          { key: 'Vendor', label: 'Vendor', count: counts.vendor },
          { key: 'Consultant', label: 'Consultant', count: counts.consultant },
          { key: 'Legal', label: 'Legal', count: counts.legal },
          { key: 'Bank', label: 'Bank', count: counts.bank },
          { key: 'Government', label: 'Gov', count: counts.government },
          { key: 'Family Member', label: 'Family', count: counts.familyMember },
          { key: 'General', label: 'General', count: counts.general },
        ];
      },
    });
  }

  loadMore(): void {
    if (this.hasMore && !this.loadingMore) {
      this.page++;
      this.loadContacts(true);
    }
  }

  // ═══════════════════════════════════
  // Search & Filters
  // ═══════════════════════════════════

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.search = '';
    this.searchSubject.next('');
    this.searchInput?.nativeElement?.focus();
  }

  setFilter(key: string): void {
    this.activeFilter = key;
    this.page = 1;
    this.contacts = [];
    this.loadContacts();
  }

  // ═══════════════════════════════════
  // Contact Selection
  // ═══════════════════════════════════

  selectContact(contact: PhonebookContactVM): void {
    this.selectedContact = contact;
    this.view = 'detail';
    this.loadNotes(contact);
  }

  backToList(): void {
    this.view = 'list';
    this.selectedContact = null;
    this.notes = [];
    this.newNoteBody = '';
  }

  // ═══════════════════════════════════
  // Notes thread
  // ═══════════════════════════════════

  private loadNotes(contact: PhonebookContactVM): void {
    this.notes = [];
    // Synthetic form-contact rows have negative ids — no notes backend.
    if (contact.isFormContact || contact.id < 0) return;

    this.notesLoading = true;
    this.phonebookService.listNotes(contact.id)
      .pipe(finalize(() => (this.notesLoading = false)))
      .subscribe({
        next: (notes) => (this.notes = notes),
        error: () => (this.notes = []),
      });
  }

  addNote(): void {
    const body = (this.newNoteBody || '').trim();
    if (!body || !this.selectedContact || this.selectedContact.isFormContact) return;

    this.addingNote = true;
    this.phonebookService.addNote(this.selectedContact.id, { body })
      .pipe(finalize(() => (this.addingNote = false)))
      .subscribe({
        next: (note) => {
          this.notes = [note, ...this.notes];
          this.newNoteBody = '';
          this.toastService.success('Note added');
        },
        error: () => this.toastService.error('Failed to add note'),
      });
  }

  deleteNote(note: PhonebookContactNoteVM): void {
    if (!this.selectedContact) return;
    this.phonebookService.deleteNote(this.selectedContact.id, note.id).subscribe({
      next: () => {
        this.notes = this.notes.filter((n) => n.id !== note.id);
      },
      error: () => this.toastService.error('Failed to delete note'),
    });
  }

  /** Human-friendly relative time ("just now", "3h ago", "yesterday", "Mar 12"). */
  formatNoteTime(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return 'yesterday';
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // ─── Pick contact (for use in other pages) ───
  pickContact(contact: PhonebookContactVM): void {
    this.sidebarService.pickContact(contact);
    this.sidebarService.close();
    this.toastService.info(`Selected ${contact.firstName} ${contact.lastName || ''}`);
  }

  // ═══════════════════════════════════
  // CRUD Actions
  // ═══════════════════════════════════

  /**
   * Dialog config that adapts to the viewport: a centred card on desktop,
   * a bottom-anchored full-width sheet on phones (thumb-friendly, app-like).
   */
  private contactDialogConfig() {
    const isPhone = typeof window !== 'undefined'
      && window.matchMedia('(max-width: 600px)').matches;

    if (isPhone) {
      // A fixed-height bottom-sheet so the flex layout inside (sticky
      // header + scrollable body + sticky footer) actually works — with
      // `height: 'auto'` the dialog grows past the viewport on long tabs
      // and the Save button slips off-screen.
      return {
        width: '100vw',
        maxWidth: '100vw',
        height: '92vh',
        maxHeight: '92vh',
        position: { bottom: '0' },
        panelClass: ['bc-dialog-panel', 'bc-dialog-panel--sheet'],
        autoFocus: false,
      };
    }
    return {
      width: 'min(680px, 96vw)',
      maxWidth: '96vw',
      maxHeight: '90vh',
      panelClass: 'bc-dialog-panel',
      autoFocus: false,
    };
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      ...this.contactDialogConfig(),
      data: { mode: 'create' } as ContactFormDialogData,
    });

    dialogRef.afterClosed().subscribe((result: CreatePhonebookContactDTO | { openExisting: PhonebookContactVM } | null) => {
      // User clicked a "possible duplicate" in the banner — open that contact
      // for editing instead of creating a new one.
      if (result && (result as any).openExisting) {
        this.openEditDialog((result as any).openExisting);
        return;
      }
      if (result) {
        this.phonebookService.create(result as CreatePhonebookContactDTO).subscribe({
          next: () => {
            this.toastService.success('Contact created');
            this.page = 1;
            this.contacts = [];
            this.loadContacts();
            this.loadCounts();
          },
          error: () => this.toastService.error('Failed to create contact'),
        });
      }
    });
  }

  openEditDialog(contact: PhonebookContactVM): void {
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      ...this.contactDialogConfig(),
      data: { contact, mode: 'edit' } as ContactFormDialogData,
    });

    dialogRef.afterClosed().subscribe((result: UpdatePhonebookContactDTO | { openExisting: PhonebookContactVM } | null) => {
      // Switch to editing a different contact from the duplicate banner.
      if (result && (result as any).openExisting) {
        this.openEditDialog((result as any).openExisting);
        return;
      }
      if (result) {
        this.phonebookService.update(contact.id, result as UpdatePhonebookContactDTO).subscribe({
          next: (updated) => {
            this.toastService.success('Contact updated');
            // Update in local list
            const idx = this.contacts.findIndex((c) => c.id === contact.id);
            if (idx >= 0) this.contacts[idx] = updated;
            // Update detail view if showing
            if (this.selectedContact?.id === contact.id) {
              this.selectedContact = updated;
            }
            this.loadCounts();
          },
          error: () => this.toastService.error('Failed to update contact'),
        });
      }
    });
  }

  openDeleteDialog(contact: PhonebookContactVM): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '420px',
      panelClass: 'bc-dialog-panel',
      autoFocus: false,
      data: { contact } as DeleteConfirmDialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.phonebookService.delete(contact.id).subscribe({
          next: () => {
            this.toastService.success('Contact deleted');
            this.contacts = this.contacts.filter((c) => c.id !== contact.id);
            this.total--;
            if (this.selectedContact?.id === contact.id) {
              this.backToList();
            }
            this.loadCounts();
          },
          error: () => this.toastService.error('Failed to delete contact'),
        });
      }
    });
  }

  // ═══════════════════════════════════
  // Quick Actions
  // ═══════════════════════════════════

  /** Strip a phone string to digits and leading +. */
  cleanPhone(phone: string | null | undefined): string {
    if (!phone) return '';
    return String(phone).replace(/[^\d+]/g, '');
  }

  /** Digits-only (no +), for WhatsApp wa.me links. */
  private digitsOnly(phone: string | null | undefined): string {
    if (!phone) return '';
    return String(phone).replace(/\D/g, '');
  }

  call3CX(phone: string | null | undefined): void {
    const cleaned = this.cleanPhone(phone);
    if (cleaned) window.location.href = `tel:${cleaned}`;
  }

  callWhatsApp(phone: string | null | undefined): void {
    const digits = this.digitsOnly(phone);
    if (digits) window.open(`https://wa.me/${digits}`, '_blank');
  }

  callTelegram(phone: string | null | undefined): void {
    const cleaned = this.cleanPhone(phone);
    if (cleaned) window.open(`https://t.me/${cleaned}`, '_blank');
  }

  emailContact(contact: PhonebookContactVM): void {
    if (contact.email) {
      window.open(`mailto:${contact.email}`, '_self');
    }
  }

  shareContact(contact: PhonebookContactVM, channel: 'clipboard' | 'email' | 'whatsapp' | 'telegram'): void {
    const text = this.buildContactText(contact);
    switch (channel) {
      case 'clipboard':
        navigator.clipboard.writeText(text).then(
          () => this.toastService.success('Contact copied to clipboard'),
          () => this.toastService.error('Failed to copy'),
        );
        break;
      case 'email':
        const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
        const subject = encodeURIComponent(`Contact details: ${name}`);
        const body = encodeURIComponent(text);
        window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=&text=${encodeURIComponent(text)}`, '_blank');
        break;
    }
  }

  private buildContactText(c: PhonebookContactVM): string {
    const lines: string[] = [];
    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    if (name) lines.push(name);
    if (c.company) lines.push(`Company: ${c.company}`);
    if (c.jobTitle) lines.push(`Title: ${c.jobTitle}`);
    if (c.mobilePhone) lines.push(`Mobile: ${c.mobilePhone}`);
    if (c.workPhone) lines.push(`Work: ${c.workPhone}`);
    if (c.email) lines.push(`Email: ${c.email}`);
    if (c.tic) lines.push(`TIC: ${c.tic}`);
    if (c.vatNumber) lines.push(`VAT: ${c.vatNumber}`);
    if (c.address || c.city || c.country) {
      const addr = [c.address, c.city, c.country].filter(v => v?.trim()).join(', ');
      if (addr) lines.push(`Address: ${addr}`);
    }
    return lines.join('\n');
  }

  // ═══════════════════════════════════
  // Sidebar Actions
  // ═══════════════════════════════════

  close(): void {
    this.sidebarService.close();
  }

  onBackdropClick(): void {
    this.close();
  }

  // ═══════════════════════════════════
  // Helpers
  // ═══════════════════════════════════

  getInitials(contact: PhonebookContactVM): string {
    const f = contact.firstName?.[0] || '';
    const l = contact.lastName?.[0] || '';
    return (f + l).toUpperCase() || '?';
  }

  getAvatarHue(contact: PhonebookContactVM): number {
    const hues = [210, 180, 260, 330, 150, 30, 0, 45, 290, 120];
    return hues[(contact.firstName || '').charCodeAt(0) % hues.length];
  }

  getSubtitle(contact: PhonebookContactVM): string {
    if (contact.jobTitle && contact.company) {
      return `${contact.jobTitle} · ${contact.company}`;
    }
    return contact.company || contact.jobTitle || contact.email || '';
  }

  trackByContact(_: number, c: PhonebookContactVM): number {
    return c.id;
  }

  // Infinite scroll handler
  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      this.loadMore();
    }
  }

  // ═══════════════════════════════════
  // Type Colors
  // ═══════════════════════════════════

  private typeColorMap: Record<string, { dot: string; bg: string; border: string; text: string }> = {
    Work:            { dot: '#4296ff', bg: '#e8f1ff', border: '#b8d4ff', text: '#1d5cbf' },
    Client:          { dot: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46' },
    Employee:        { dot: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', text: '#5b21b6' },
    Vendor:          { dot: '#f59e0b', bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
    Consultant:      { dot: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc', text: '#155e75' },
    Legal:           { dot: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3' },
    Bank:            { dot: '#14b8a6', bg: '#f0fdfa', border: '#99f6e4', text: '#115e59' },
    Government:      { dot: '#f97316', bg: '#fff7ed', border: '#fed7aa', text: '#9a3412' },
    'Family Member': { dot: '#ec4899', bg: '#fdf2f8', border: '#fbcfe8', text: '#9d174d' },
    General:         { dot: '#9ba3b0', bg: '#f8f9fb', border: '#e2e5eb', text: '#5f6775' },
  };

  getTypeColor(type: string): string {
    return this.typeColorMap[type]?.dot || '#9ba3b0';
  }

  getTypeBadgeBg(type: string): string {
    return this.typeColorMap[type]?.bg || '#f8f9fb';
  }

  getTypeBadgeBorder(type: string): string {
    return this.typeColorMap[type]?.border || '#e2e5eb';
  }

  getTypeBadgeText(type: string): string {
    return this.typeColorMap[type]?.text || '#5f6775';
  }

  /** Material icon name for each preferred-contact channel. */
  contactMethodIcon(value: string): string {
    switch (value) {
      case 'Phone':     return 'phone';
      case 'WhatsApp':  return 'chat';
      case 'SMS':       return 'sms';
      case 'Email':     return 'email';
      case 'LinkedIn':  return 'work';
      case 'Telegram':  return 'send';
      case 'Signal':    return 'shield';
      case 'In person': return 'handshake';
      default:          return 'more_horiz';
    }
  }
}
